"use client";

import InAppBrowserGoogleBanner from "@/components/auth/InAppBrowserGoogleBanner";
import { isNaverLoginEnabled } from "@/lib/config/naverLogin";

export type SnsProvider = "google" | "kakao" | "naver" | null;

interface SnsAuthButtonsProps {
  snsLoading: SnsProvider;
  onGoogle: () => void;
  onKakao: () => void;
  onNaver: () => void;
  mode?: "login" | "signup";
  layout?: "full" | "icons";
  showGoogleBanner?: boolean;
  className?: string;
}

function GoogleIcon({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      className={compact ? "w-6 h-6" : "w-5 h-5 mr-3 shrink-0"}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      />
    </svg>
  );
}

function KakaoIcon({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      className={compact ? "w-6 h-6" : "w-5 h-5 mr-3 shrink-0"}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M12 3C6.48 3 2 6.58 2 11c0 2.84 1.87 5.35 4.69 6.84L5.5 21l3.94-2.17c.82.12 1.66.19 2.56.19 5.52 0 10-3.58 10-8s-4.48-8-10-8z"
      />
    </svg>
  );
}

function NaverIcon({ compact = false }: { compact?: boolean }) {
  return (
    <svg
      className={compact ? "w-5 h-5" : "w-5 h-5 mr-3 shrink-0"}
      viewBox="0 0 24 24"
      aria-hidden
    >
      <path
        fill="currentColor"
        d="M16.273 12.845L7.376 0H0v24h7.727V11.156L16.624 24H24V0h-7.727v12.845z"
      />
    </svg>
  );
}

function LoadingSpinner({ className = "border-gray-600" }: { className?: string }) {
  return (
    <div
      className={`animate-spin rounded-full h-5 w-5 border-b-2 ${className}`}
    />
  );
}

export default function SnsAuthButtons({
  snsLoading,
  onGoogle,
  onKakao,
  onNaver,
  mode = "login",
  layout = "icons",
  showGoogleBanner = true,
  className = "",
}: SnsAuthButtonsProps) {
  const isSignup = mode === "signup";
  const naverEnabled = isNaverLoginEnabled();
  const googleLabel = isSignup ? "Google로 계속하기" : "구글로 로그인";
  const kakaoLabel = isSignup ? "카카오로 계속하기" : "카카오로 로그인";
  const naverLabel = isSignup ? "네이버로 계속하기" : "네이버로 로그인";
  const buttonBase = isSignup
    ? "w-full flex justify-center items-center px-4 py-3.5 rounded-xl text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition-colors"
    : "w-full flex justify-center items-center px-4 py-3 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors";

  if (layout === "icons") {
    const iconButton =
      "flex flex-col items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed";
    const iconCircle =
      "w-12 h-12 rounded-full flex items-center justify-center shadow-sm transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2";

    return (
      <div className={className}>
        {showGoogleBanner && <InAppBrowserGoogleBanner />}

        <div className="flex items-start justify-center gap-6">
          <button
            type="button"
            onClick={onGoogle}
            disabled={snsLoading !== null}
            aria-label={googleLabel}
            className={iconButton}
          >
            <span
              className={`${iconCircle} border border-gray-200 bg-white hover:bg-gray-50 focus:ring-blue-500`}
            >
              {snsLoading === "google" ? (
                <LoadingSpinner />
              ) : (
                <GoogleIcon compact />
              )}
            </span>
            <span className="text-xs text-gray-500">구글</span>
          </button>

          <button
            type="button"
            onClick={onKakao}
            disabled={snsLoading !== null}
            aria-label={kakaoLabel}
            className={iconButton}
          >
            <span
              className={`${iconCircle} bg-[#FEE500] text-[#191919] hover:bg-[#F5DC00] focus:ring-yellow-400`}
            >
              {snsLoading === "kakao" ? (
                <LoadingSpinner className="border-[#191919]" />
              ) : (
                <KakaoIcon compact />
              )}
            </span>
            <span className="text-xs text-gray-500">카카오</span>
          </button>

          {naverEnabled && (
            <button
              type="button"
              onClick={onNaver}
              disabled={snsLoading !== null}
              aria-label={naverLabel}
              className={iconButton}
            >
              <span
                className={`${iconCircle} bg-[#03C75A] text-white hover:bg-[#02b351] focus:ring-green-500`}
              >
                {snsLoading === "naver" ? (
                  <LoadingSpinner className="border-white" />
                ) : (
                  <NaverIcon compact />
                )}
              </span>
              <span className="text-xs text-gray-500">네이버</span>
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {showGoogleBanner && <InAppBrowserGoogleBanner />}

      <button
        type="button"
        onClick={onGoogle}
        disabled={snsLoading !== null}
        className={`${buttonBase} border-gray-300 text-gray-800 bg-white hover:bg-gray-50 focus:ring-blue-500`}
      >
        {snsLoading === "google" ? (
          <LoadingSpinner />
        ) : (
          <>
            <GoogleIcon />
            {googleLabel}
          </>
        )}
      </button>

      <button
        type="button"
        onClick={onKakao}
        disabled={snsLoading !== null}
        className={`${buttonBase} border-[#FEE500] bg-[#FEE500] text-[#191919] hover:bg-[#F5DC00] focus:ring-yellow-400`}
      >
        {snsLoading === "kakao" ? (
          <LoadingSpinner className="border-[#191919]" />
        ) : (
          <>
            <KakaoIcon />
            {kakaoLabel}
          </>
        )}
      </button>

      {naverEnabled && (
        <button
          type="button"
          onClick={onNaver}
          disabled={snsLoading !== null}
          className={`${buttonBase} border-[#03C75A] bg-[#03C75A] text-white hover:bg-[#02b351] focus:ring-green-500`}
        >
          {snsLoading === "naver" ? (
            <LoadingSpinner className="border-white" />
          ) : (
            <>
              <NaverIcon />
              {naverLabel}
            </>
          )}
        </button>
      )}
    </div>
  );
}
